import { z } from "zod";

import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { getPictureCharacterFeedbackCue } from "@/audio/curriculum-catalog/chinese-feedback-cues";
import { isB4ChinesePlayerAsset } from "@/assets/requirements/chinese/b4-chinese-player";
import type { PictureCharacterMatchPayload } from "@/content/chinese";
import {
  boundedTextSchema,
  kebabCaseIdentifierSchema,
  localResourceReferenceSchema
} from "@/domain/schemas";
import {
  advanceHintEscalation,
  type ActiveHintStage
} from "@/game/feedback";
import {
  getAdjacentId,
  type InputMethod,
  type NavigationDirection
} from "@/game/input";
import {
  choiceMatchItemSchema,
  createMatchingActivityState,
  createMatchingItemKey,
  getMatchingKeyboardOrder,
  matchingPayloadSchema,
  reduceMatchingActivity,
  type MatchingActivityState,
  type MatchingItemKey,
  type MatchingItemReference,
  type MatchingPayload
} from "@/game/templates/choice-match";

import type { PictureCharacterMatchLevel } from "../levels";

const pictureItemSchema = z.strictObject({
  id: kebabCaseIdentifierSchema,
  asset: localResourceReferenceSchema,
  alt: boundedTextSchema(120)
});

const pictureCharacterAnswerSchema = z.strictObject({
  pictureId: kebabCaseIdentifierSchema,
  characterId: kebabCaseIdentifierSchema
});

const pictureCharacterPayloadSchema = z.strictObject({
  pictureItems: z.array(pictureItemSchema).min(2).max(12),
  characterItems: z.array(choiceMatchItemSchema).min(2).max(12),
  matches: z.array(pictureCharacterAnswerSchema).min(2).max(12),
  hint: boundedTextSchema(240)
});

function toMatchingPayload(
  payload: PictureCharacterMatchPayload
): MatchingPayload {
  return {
    kind: "matching",
    leftItems: payload.pictureItems.map(({ id, alt }) => ({
      id,
      label: alt
    })),
    rightItems: payload.characterItems,
    pairs: payload.matches.map(({ pictureId, characterId }) => ({
      leftId: pictureId,
      rightId: characterId
    }))
  };
}

function parsePictureCharacterPayload(
  level: PictureCharacterMatchLevel,
  value: unknown
): PictureCharacterMatchPayload {
  const payload = pictureCharacterPayloadSchema.parse(value);

  for (const item of payload.pictureItems) {
    if (
      !level.assets.includes(item.asset) ||
      !isB4ChinesePlayerAsset(level.id, item.asset, "image")
    ) {
      throw new Error(
        "Picture asset is not declared by the B4 level manifest: " +
          item.asset
      );
    }
  }

  matchingPayloadSchema.parse(toMatchingPayload(payload));
  return payload;
}

export interface PictureCharacterActivityState {
  readonly activity: MatchingActivityState;
}

export type PictureCharacterAction =
  | {
      readonly type: "select-item";
      readonly item: MatchingItemReference;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "focus-item";
      readonly itemKey: MatchingItemKey;
    }
  | {
      readonly type: "move-focus";
      readonly direction: NavigationDirection;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "request-hint" }
  | { readonly type: "replay" };

interface BaseMatchItemRenderModel {
  readonly disabled: boolean;
  readonly focused: boolean;
  readonly hinted: boolean;
  readonly id: string;
  readonly itemKey: MatchingItemKey;
  readonly matched: boolean;
  readonly selected: boolean;
  readonly visible: boolean;
}

export interface PictureMatchRenderModel extends BaseMatchItemRenderModel {
  readonly alt: string;
  readonly imageSrc: string;
}

export interface CharacterMatchRenderModel extends BaseMatchItemRenderModel {
  readonly label: string;
}

export interface PictureCharacterFeedbackRenderModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly message: string;
  readonly playbackRequest: number;
  readonly stage: ActiveHintStage;
}

export interface PictureCharacterRenderModel {
  readonly characters: readonly CharacterMatchRenderModel[];
  readonly feedback: PictureCharacterFeedbackRenderModel | null;
  readonly focusedItemKey: MatchingItemKey | null;
  readonly keyboardOrder: readonly MatchingItemKey[];
  readonly pictures: readonly PictureMatchRenderModel[];
  readonly progress: {
    readonly matched: number;
    readonly total: number;
  };
}

function createPictureCharacterState(): PictureCharacterActivityState {
  return { activity: createMatchingActivityState() };
}

function isMatchedPair(
  state: PictureCharacterActivityState,
  pictureId: string,
  characterId: string
): boolean {
  return state.activity.matchedPairs.some(
    (pair) => pair.leftId === pictureId && pair.rightId === characterId
  );
}

function getHintPair(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload
): PictureCharacterMatchPayload["matches"][number] | undefined {
  const selected = state.activity.selectedItem;

  if (selected !== null) {
    return payload.matches.find((pair) =>
      selected.side === "left"
        ? pair.pictureId === selected.itemId
        : pair.characterId === selected.itemId
    );
  }

  return payload.matches.find(
    (pair) =>
      !isMatchedPair(state, pair.pictureId, pair.characterId)
  );
}

function getAvailableKeyboardOrder(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload
): readonly MatchingItemKey[] {
  const templatePayload = toMatchingPayload(payload);
  const fullOrder = getMatchingKeyboardOrder(
    templatePayload,
    state.activity.matchedPairs
  );
  const stage = state.activity.hintState.stage;

  if (stage === "none" || stage === "gentle-hint") {
    return fullOrder;
  }

  const hintPair = getHintPair(state, payload);
  if (hintPair === undefined) {
    return fullOrder;
  }

  const availableKeys = new Set<MatchingItemKey>([
    createMatchingItemKey({
      side: "left",
      itemId: hintPair.pictureId
    }),
    createMatchingItemKey({
      side: "right",
      itemId: hintPair.characterId
    })
  ]);

  return fullOrder.filter((itemKey) => availableKeys.has(itemKey));
}

function selectItem(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload,
  item: MatchingItemReference,
  inputMethod: InputMethod
): PictureCharacterActivityState {
  const itemKey = createMatchingItemKey(item);

  if (!getAvailableKeyboardOrder(state, payload).includes(itemKey)) {
    return state;
  }

  return {
    activity: reduceMatchingActivity(
      toMatchingPayload(payload),
      state.activity,
      {
        type: "select-item",
        item,
        inputMethod
      }
    )
  };
}

function findItemReference(
  payload: PictureCharacterMatchPayload,
  itemKey: MatchingItemKey
): MatchingItemReference | null {
  const picture = payload.pictureItems.find(
    ({ id }) =>
      createMatchingItemKey({ side: "left", itemId: id }) === itemKey
  );

  if (picture !== undefined) {
    return { side: "left", itemId: picture.id };
  }

  const character = payload.characterItems.find(
    ({ id }) =>
      createMatchingItemKey({ side: "right", itemId: id }) === itemKey
  );

  return character === undefined
    ? null
    : { side: "right", itemId: character.id };
}

function reducePictureCharacter(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload,
  action: PictureCharacterAction
): PictureCharacterActivityState {
  switch (action.type) {
    case "select-item":
      return selectItem(state, payload, action.item, action.inputMethod);
    case "focus-item":
      if (!getAvailableKeyboardOrder(state, payload).includes(action.itemKey)) {
        return state;
      }

      return {
        activity: {
          ...state.activity,
          focusedItemKey: action.itemKey,
          lastInputMethod: "keyboard"
        }
      };
    case "move-focus": {
      const focusedItemKey = getAdjacentId(
        getAvailableKeyboardOrder(state, payload),
        state.activity.focusedItemKey,
        action.direction
      );

      if (focusedItemKey === null) {
        return state;
      }

      return {
        activity: {
          ...state.activity,
          focusedItemKey,
          lastInputMethod: "keyboard"
        }
      };
    }
    case "activate-focused": {
      if (state.activity.focusedItemKey === null) {
        return state;
      }

      const item = findItemReference(
        payload,
        state.activity.focusedItemKey
      );
      return item === null
        ? state
        : selectItem(state, payload, item, "keyboard");
    }
    case "request-hint": {
      const hintState = advanceHintEscalation(state.activity.hintState);
      const nextState = {
        activity: {
          ...state.activity,
          feedback: null,
          hintState
        }
      };
      const availableOrder = getAvailableKeyboardOrder(nextState, payload);

      return {
        activity: {
          ...nextState.activity,
          focusedItemKey:
            state.activity.focusedItemKey !== null &&
            availableOrder.includes(state.activity.focusedItemKey)
              ? state.activity.focusedItemKey
              : null
        }
      };
    }
    case "replay":
      return createPictureCharacterState();
  }
}

function getPictureCharacterMetrics(
  state: PictureCharacterActivityState
): ActivityCompletionMetrics {
  const completionScore = state.activity.completionScore;
  const completed =
    state.activity.status === "complete" && completionScore !== null;

  return {
    completed,
    score: completed ? completionScore.score : 0,
    correctRate: completed ? completionScore.correctRate : 0,
    hintCount: state.activity.hintState.hintCount,
    retryCount: state.activity.retryCount
  };
}

function getFeedback(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload,
  levelId: string
): PictureCharacterFeedbackRenderModel | null {
  if (state.activity.feedback?.kind === "retry") {
    const cue = getPictureCharacterFeedbackCue(
      levelId,
      payload,
      state.activity.feedback.hintStage,
      "retry"
    );

    return {
      audioPath: cue.audioPath,
      kind: "retry",
      message: cue.text,
      playbackRequest:
        state.activity.hintState.hintCount + state.activity.retryCount,
      stage: state.activity.feedback.hintStage
    };
  }

  const stage = state.activity.hintState.stage;
  if (stage === "none") {
    return null;
  }

  const cue = getPictureCharacterFeedbackCue(
    levelId,
    payload,
    stage,
    "hint"
  );

  return {
    audioPath: cue.audioPath,
    kind: "hint",
    message: cue.text,
    playbackRequest:
      state.activity.hintState.hintCount + state.activity.retryCount,
    stage
  };
}

function toPictureCharacterRenderModel(
  state: PictureCharacterActivityState,
  payload: PictureCharacterMatchPayload,
  levelId: string
): PictureCharacterRenderModel {
  const keyboardOrder = getAvailableKeyboardOrder(state, payload);
  const availableKeys = new Set(keyboardOrder);
  const matchedPictureIds = new Set(
    state.activity.matchedPairs.map(({ leftId }) => leftId)
  );
  const matchedCharacterIds = new Set(
    state.activity.matchedPairs.map(({ rightId }) => rightId)
  );
  const replayHint =
    state.activity.hintState.stage === "replay-demonstration";

  return {
    pictures: payload.pictureItems.map((item) => {
      const itemKey = createMatchingItemKey({
        side: "left",
        itemId: item.id
      });
      const matched = matchedPictureIds.has(item.id);

      return {
        alt: item.alt,
        disabled: matched || state.activity.status === "complete",
        focused: state.activity.focusedItemKey === itemKey,
        hinted: replayHint && availableKeys.has(itemKey),
        id: item.id,
        imageSrc: item.asset,
        itemKey,
        matched,
        selected:
          state.activity.selectedItem?.side === "left" &&
          state.activity.selectedItem.itemId === item.id,
        visible: matched || availableKeys.has(itemKey)
      };
    }),
    characters: payload.characterItems.map((item) => {
      const itemKey = createMatchingItemKey({
        side: "right",
        itemId: item.id
      });
      const matched = matchedCharacterIds.has(item.id);

      return {
        disabled: matched || state.activity.status === "complete",
        focused: state.activity.focusedItemKey === itemKey,
        hinted: replayHint && availableKeys.has(itemKey),
        id: item.id,
        itemKey,
        label: item.label,
        matched,
        selected:
          state.activity.selectedItem?.side === "right" &&
          state.activity.selectedItem.itemId === item.id,
        visible: matched || availableKeys.has(itemKey)
      };
    }),
    feedback: getFeedback(state, payload, levelId),
    focusedItemKey: state.activity.focusedItemKey,
    keyboardOrder,
    progress: {
      matched: state.activity.matchedPairs.length,
      total: payload.matches.length
    }
  };
}

export function createPictureCharacterAdapter(
  level: PictureCharacterMatchLevel
): ActivityAdapter<
  PictureCharacterMatchPayload,
  PictureCharacterActivityState,
  PictureCharacterAction,
  PictureCharacterRenderModel
> {
  if (
    !isB4ChinesePlayerAsset(level.id, level.promptAudio, "audio")
  ) {
    throw new Error(
      "Picture-character narration is missing from the B4 manifest"
    );
  }

  return {
    activityType: "picture-character-match",
    parsePayload: (value) => parsePictureCharacterPayload(level, value),
    createInitialState: (payload, context) => {
      void payload;
      void context;
      return createPictureCharacterState();
    },
    reduce: (state, payload, action, context) => {
      void context;
      return reducePictureCharacter(state, payload, action);
    },
    getMetrics: (state, payload) => {
      void payload;
      return getPictureCharacterMetrics(state);
    },
    toRenderModel: (state, payload) =>
      toPictureCharacterRenderModel(state, payload, level.id)
  };
}

export const pictureCharacterHostActions = {
  requestHint: () => ({ type: "request-hint" }) as const,
  replay: () => ({ type: "replay" }) as const
};
